/**
 * Merges API `tokens` with defaults, then derives as much as possible from a small palette
 * (ideally 6 colors: brand, brand-deep, surface, text, footer bg, footer text).
 * Accents (CTA, gradients, focus rings, map gold, pin rings) always follow primary / primary_deep.
 */

const CANONICAL = {
  primary: "#ea580c",
  primary_mid: "#f97316",
  primary_deep: "#c2410c",
  primary_soft_bg: "#ffedd5",
  primary_subtle_bg: "#fff7ed",
  primary_subtle_border: "#fed7aa",
  primary_emphasis_text: "#9a3412",
  gold_hover_light: "#fb923c",
  gradient_primary: "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)",
  gradient_horizontal: "linear-gradient(90deg, #ea580c 0%, #f97316 45%, #c2410c 100%)",
  gradient_ribbon: "linear-gradient(90deg, #ea580c 0%, #c2410c 50%, #ea580c 100%)",
  shadow_gold: "0 4px 16px rgba(194, 65, 12, 0.22)",
  text_primary: "#0a0a0a",
  text_muted: "#525252",
  text_ink_strong: "#0c1222",
  text_ink_soft: "#334155",
  text_secondary: "#64748b",
  ui_text: "#222222",
  line_brand: "rgba(234, 88, 12, 0.22)",
  line_subtle: "rgba(15, 23, 42, 0.08)",
  border_default: "#dddddd",
  chip_border: "rgba(234, 88, 12, 0.18)",
  surface_page: "#faf9f7",
  panel_glass: "linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(250,249,247,0.72) 100%)",
  chip_bg: "rgba(250, 248, 244, 0.92)",
  cta_primary: "#ea580c",
  cta_primary_hover: "#c2410c",
  cta_on_primary: "#0a0a0a",
  button_disabled_bg: "#94a3b8",
  bs_primary: "#ea580c",
  bs_primary_rgb: "234, 88, 12",
  footer_bg: "#0c0c0c",
  footer_bg_soft: "#121212",
  footer_text: "#b9b6b0",
  footer_heading: "#f5f2eb",
  footer_muted: "#6e6b66",
  footer_border: "rgba(234, 88, 12, 0.22)",
  footer_accent: "#ea580c",
  footer_accent_hover: "#fb923c",
  map_ui_gold: "#ea580c",
  map_ui_gold_deep: "#9a3412",
  map_ui_surface: "rgba(255, 255, 255, 0.94)",
  map_ui_surface_2: "rgba(252, 250, 246, 0.98)",
  map_ui_ring: "rgba(234, 88, 12, 0.32)",
  shadow_ink_sm: "0 1px 2px rgba(15, 23, 42, 0.06)",
  shadow_ink_md: "0 4px 14px rgba(15, 23, 42, 0.08)",
  shadow_ink_lg: "0 14px 40px rgba(15, 23, 42, 0.12)",
  accent_warm_gradient: "linear-gradient(135deg, #c2410c 0%, #ea580c 55%, #f97316 100%)",
  nav_frost: "rgba(255, 255, 255, 0.9)",
  nav_brand_wash: "linear-gradient(90deg, rgba(234, 88, 12, 0.1) 0%, rgba(194, 65, 12, 0.08) 100%)",
  map_background_color: "#f5f3ef",
  map_v2_shell_bg: "#0E0F11",
  map_v2_canvas_bg: "#E8ECEF",
  map_v2_panel_bg: "#16181B",
  map_v2_panel_chip_bg: "#3a3f46",
  map_v2_row_bg: "#1F2226",
  map_v2_row_text: "#e8eaed",
  map_v2_row_muted: "#9aa0a6",
  map_v2_pin_1: "#5BA8FF",
  map_v2_pin_2: "#7DD49A",
  map_v2_pin_3: "#F4A05B",
  map_v2_pin_4: "#C28BE6",
  pin_stroke_light: "#f8fafc",
  pin_stroke_gold: "rgba(234, 88, 12, 0.72)",
  pin_logo_port_center: "#ffffff",
  pin_logo_port_edge: "#f0ece4",
  pin_ring_idle: "#ea580c",
  pin_ring_active: "#fdba74",
  pin_ring_bronze: "#7c2d12",
  pin_ring_deep: "#c2410c",

  /* Component surfaces & neutrals (override via API `tokens`; many derived if omitted) */
  surface_white: "#ffffff",
  surface_muted_panel: "#f8f9fa",
  surface_image_placeholder: "#f5f5f5",
  border_neutral: "#e0e0e0",
  border_strong: "#333333",
  accent_marketing_cyan: "#089ccd",
  accent_marketing_orange: "#c35f01",
  oauth_facebook: "#1877f2",
  info_surface: "#e0f2fe",
  info_ink: "#0c4a6e",
  info_surface_hover: "#bae6fd",
  teal_deep: "#087B7B",
  auth_panel_dark_1: "#1a1a1a",
  auth_panel_dark_2: "#2d1a0a",
  auth_panel_dark_3: "#1f140a",
  theme_text_cream: "#faf8f3",
  line_extralight: "#eeeeee",
  line_light_gray: "#e5e5e5",
  surface_gray_soft: "#f7f7f7",
  border_gray_mid: "#b0b0b0",
  surface_warm_gray: "#ebe8e4",
  text_gray_mid: "#888888",
  state_error_bg: "#ffebee",
  state_error_ink: "#c62828",
  slate_50: "#f8fafc",
  slate_200: "#e2e8f0",
  slate_600: "#475569",
  slate_700: "#334155",
  slate_800: "#1e293b",
  slate_750: "#2d3748",
  cool_blue_tint: "#e0f4fc",
  surface_gray_f1: "#f1f1f1",
  text_ui_555: "#555555",
  state_error_bright: "#d32f2f",
  state_success_from: "#10b981",
  state_success_to: "#059669",
  state_error_from: "#ef4444",
  state_error_to: "#dc2626",
  state_error_tint_1: "#fef2f2",
  state_error_tint_2: "#fee2e2",
  gradient_state_success: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
  gradient_state_error: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
  gradient_state_error_soft: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
  state_error_border_soft: "#fca5a5",
  slate_100: "#f1f5f9",
  slate_300_ui: "#cbd5e1",
  accent_teal_700: "#0f766e",
  services_card_bg: "#26324d",
  services_cta_bg: "#3a4b6b",
  services_cta_hover: "#455a7a",
  toast_shadow_success: "0 20px 40px rgba(16, 185, 129, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
  toast_shadow_error: "0 20px 40px rgba(239, 68, 68, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
};

function isProvided(v) {
  return v != null && String(v).trim() !== "";
}

function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex).trim());
  if (!m) return null;
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
}

function rgbToHex(r, g, b) {
  const c = (n) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${c(r).toString(16).padStart(2, "0")}${c(g).toString(16).padStart(2, "0")}${c(b).toString(16).padStart(2, "0")}`;
}

function mixHex(hexA, hexB, t) {
  const A = hexToRgb(hexA);
  const B = hexToRgb(hexB);
  if (!A || !B) return isProvided(hexA) ? String(hexA).trim() : String(hexB || "#000000").trim();
  const r = A.r + (B.r - A.r) * t;
  const gCh = A.g + (B.g - A.g) * t;
  const bCh = A.b + (B.b - A.b) * t;
  return rgbToHex(r, gCh, bCh);
}

function lightenHex(hex, t) {
  return mixHex(hex, "#ffffff", t);
}

function darkenHex(hex, t) {
  return mixHex(hex, "#000000", t);
}

function rgbaFromHex(hex, alpha) {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(250, 249, 247, ${alpha})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

function rgbaFromPrimary(hex, alpha) {
  return rgbaFromHex(hex, alpha);
}

/** Layered page canvas (matches site-background.css) from API surface_page + primary */
function buildSiteBgImage(surfaceHex, primaryHex) {
  const surf = String(surfaceHex || CANONICAL.surface_page).trim();
  const p = String(primaryHex || CANONICAL.primary).trim();
  const mid = mixHex("#ffffff", surf, 0.42);
  return [
    `radial-gradient(120% 90% at 50% -10%, ${rgbaFromHex(p, 0.08)} 0%, transparent 55%)`,
    "radial-gradient(80% 60% at 100% 40%, rgba(0, 0, 0, 0.03) 0%, transparent 45%)",
    `linear-gradient(180deg, #ffffff 0%, ${mid} 50%, ${surf} 100%)`,
  ].join(", ");
}

/**
 * Fill non–brand keys from surface + ink + footer pair when the API omits them (6-color mode).
 */
function deriveOptionalSurfacesAndType(out, incoming) {
  const surf = String(out.surface_page || CANONICAL.surface_page).trim();
  const ink = String(out.text_primary || CANONICAL.text_primary).trim();
  const fbg = String(out.footer_bg || CANONICAL.footer_bg).trim();
  const ft = String(out.footer_text || CANONICAL.footer_text).trim();
  const p = String(out.primary || CANONICAL.primary).trim();
  const deep = String(out.primary_deep || CANONICAL.primary_deep).trim();

  if (!isProvided(incoming.primary_mid)) {
    out.primary_mid = mixHex(p, deep, 0.42);
  }
  if (!isProvided(incoming.gold_hover_light)) {
    out.gold_hover_light = lightenHex(p, 0.34);
  }
  if (!isProvided(incoming.primary_soft_bg)) {
    out.primary_soft_bg = lightenHex(mixHex(surf, p, 0.28), 0.02);
  }
  if (!isProvided(incoming.primary_subtle_bg)) {
    out.primary_subtle_bg = mixHex(surf, p, 0.1);
  }
  if (!isProvided(incoming.primary_subtle_border)) {
    out.primary_subtle_border = mixHex(surf, deep, 0.22);
  }
  if (!isProvided(incoming.primary_emphasis_text)) {
    out.primary_emphasis_text = darkenHex(deep, 0.1);
  }

  if (!isProvided(incoming.text_muted)) {
    out.text_muted = mixHex(ink, "#6b6b6b", 0.38);
  }
  if (!isProvided(incoming.text_ink_strong)) {
    out.text_ink_strong = ink;
  }
  if (!isProvided(incoming.text_ink_soft)) {
    out.text_ink_soft = mixHex(ink, surf, 0.32);
  }
  if (!isProvided(incoming.text_secondary)) {
    out.text_secondary = mixHex(ink, "#64748b", 0.45);
  }
  if (!isProvided(incoming.ui_text)) {
    out.ui_text = mixHex(ink, "#1c1c1c", 0.15);
  }

  if (!isProvided(incoming.panel_glass)) {
    out.panel_glass = `linear-gradient(180deg, rgba(255,255,255,0.88) 0%, ${rgbaFromHex(surf, 0.72)} 100%)`;
  }
  if (!isProvided(incoming.chip_bg)) {
    out.chip_bg = rgbaFromHex(surf, 0.92);
  }
  if (!isProvided(incoming.border_default)) {
    out.border_default = mixHex(surf, ink, 0.14);
  }
  if (!isProvided(incoming.line_subtle)) {
    out.line_subtle = rgbaFromHex(ink, 0.08);
  }

  if (!isProvided(incoming.cta_on_primary)) {
    out.cta_on_primary = ink;
  }
  if (!isProvided(incoming.button_disabled_bg)) {
    out.button_disabled_bg = CANONICAL.button_disabled_bg;
  }

  if (!isProvided(incoming.footer_bg_soft)) {
    out.footer_bg_soft = lightenHex(fbg, 0.06);
  }
  if (!isProvided(incoming.footer_heading)) {
    out.footer_heading = lightenHex(ft, 0.48);
  }
  if (!isProvided(incoming.footer_muted)) {
    out.footer_muted = mixHex(ft, fbg, 0.52);
  }

  if (!isProvided(incoming.map_background_color)) {
    out.map_background_color = mixHex(surf, deep, 0.035);
  }
  if (!isProvided(incoming.map_ui_surface)) {
    out.map_ui_surface = "rgba(255, 255, 255, 0.94)";
  }
  if (!isProvided(incoming.map_ui_surface_2)) {
    out.map_ui_surface_2 = rgbaFromHex(surf, 0.98);
  }

  if (!isProvided(incoming.pin_stroke_light)) {
    out.pin_stroke_light = mixHex("#ffffff", surf, 0.06);
  }
  if (!isProvided(incoming.pin_logo_port_center)) {
    out.pin_logo_port_center = "#ffffff";
  }
  if (!isProvided(incoming.pin_logo_port_edge)) {
    out.pin_logo_port_edge = mixHex(surf, p, 0.12);
  }
  if (!isProvided(incoming.pin_ring_active)) {
    out.pin_ring_active = lightenHex(p, 0.38);
  }
  if (!isProvided(incoming.pin_ring_bronze)) {
    out.pin_ring_bronze = mixHex(deep, "#2a1f08", 0.42);
  }

  if (!isProvided(incoming.surface_muted_panel)) {
    out.surface_muted_panel = mixHex(surf, ink, 0.035);
  }
  if (!isProvided(incoming.surface_image_placeholder)) {
    out.surface_image_placeholder = mixHex(surf, ink, 0.028);
  }
  if (!isProvided(incoming.border_neutral)) {
    out.border_neutral = mixHex(surf, ink, 0.12);
  }
  if (!isProvided(incoming.neutral_200)) {
    out.neutral_200 = mixHex(surf, ink, 0.075);
  }
  if (!isProvided(incoming.neutral_300)) {
    out.neutral_300 = mixHex(surf, ink, 0.12);
  }
  if (!isProvided(incoming.neutral_400)) {
    out.neutral_400 = mixHex(surf, ink, 0.22);
  }
  if (!isProvided(incoming.neutral_500)) {
    out.neutral_500 = mixHex(surf, ink, 0.38);
  }
  if (!isProvided(incoming.neutral_600)) {
    out.neutral_600 = mixHex(surf, ink, 0.52);
  }
  if (!isProvided(incoming.neutral_700)) {
    out.neutral_700 = mixHex(surf, ink, 0.62);
  }

  if (!isProvided(incoming.nav_frost)) {
    out.nav_frost = rgbaFromHex(surf, 0.92);
  }
}

/**
 * @param {Record<string, string>|null|undefined} raw
 * @returns {Record<string, string>}
 */
export function deriveThemeTokens(raw) {
  const incoming = raw && typeof raw === "object" ? raw : {};
  const out = { ...CANONICAL, ...incoming };

  deriveOptionalSurfacesAndType(out, incoming);

  const p = String(out.primary || CANONICAL.primary).trim();
  const deep = String(out.primary_deep || CANONICAL.primary_deep).trim();
  const mid = String(out.primary_mid || CANONICAL.primary_mid).trim();

  out.primary = p;
  out.primary_deep = deep;
  out.primary_mid = mid;

  out.gradient_primary = `linear-gradient(135deg, ${p} 0%, ${deep} 100%)`;
  out.gradient_horizontal = `linear-gradient(90deg, ${p} 0%, ${mid} 45%, ${deep} 100%)`;
  out.gradient_ribbon = `linear-gradient(90deg, ${p} 0%, ${deep} 50%, ${p} 100%)`;
  out.accent_warm_gradient = `linear-gradient(135deg, ${deep} 0%, ${p} 55%, ${mid} 100%)`;
  out.nav_brand_wash = `linear-gradient(90deg, ${rgbaFromPrimary(p, 0.1)} 0%, ${rgbaFromPrimary(deep, 0.08)} 100%)`;

  out.primary_focus_ring = rgbaFromPrimary(p, 0.28);
  out.line_brand = rgbaFromPrimary(p, 0.22);
  out.chip_border = rgbaFromPrimary(p, 0.18);
  out.footer_border = rgbaFromPrimary(p, 0.22);
  out.map_ui_ring = rgbaFromPrimary(p, 0.32);
  out.pin_stroke_gold = rgbaFromPrimary(p, 0.72);
  out.shadow_gold = `0 4px 16px ${rgbaFromPrimary(deep, 0.22)}`;

  out.cta_primary = p;
  out.cta_primary_hover = deep;
  out.bs_primary = p;
  const rgb = hexToRgb(p);
  if (rgb) {
    out.bs_primary_rgb = `${rgb.r}, ${rgb.g}, ${rgb.b}`;
  }

  out.footer_accent = p;
  out.footer_accent_hover = String(out.gold_hover_light || CANONICAL.gold_hover_light).trim();

  out.map_ui_gold = p;
  if (!isProvided(incoming.map_ui_gold_deep)) {
    out.map_ui_gold_deep = deep;
  }

  out.pin_ring_idle = p;
  out.pin_ring_deep = deep;

  const surf = String(out.surface_page || CANONICAL.surface_page).trim();
  if (!isProvided(incoming.site_bg_image)) {
    out.site_bg_image = buildSiteBgImage(surf, p);
  } else {
    out.site_bg_image = String(incoming.site_bg_image).trim();
  }

  const cyan = String(out.accent_marketing_cyan || CANONICAL.accent_marketing_cyan).trim();
  const orange = String(out.accent_marketing_orange || CANONICAL.accent_marketing_orange).trim();
  out.gradient_marketing_horizontal = `linear-gradient(to right, ${cyan}, ${orange})`;

  const a1 = String(out.auth_panel_dark_1).trim();
  const a2 = String(out.auth_panel_dark_2).trim();
  const a3 = String(out.auth_panel_dark_3).trim();
  out.auth_panel_gradient = `linear-gradient(145deg, ${a1} 0%, ${a2} 42%, ${a3} 100%)`;

  const inkHex = String(out.text_primary || CANONICAL.text_primary).trim();
  out.overlay_image_bottom = `linear-gradient(to top, ${rgbaFromHex(inkHex, 0.7)} 0%, ${rgbaFromHex(inkHex, 0.3)} 45%, transparent 100%)`;

  return out;
}
